<script lang="ts" setup>
import type { CheckProps as Props } from '@/shared/interfaces';
import useCheckbox from '@/shared/composables/use-checkbox';

type CheckboxModel = CheckboxValue | Array<unknown>;
type CheckboxValue = string | boolean | number | Record<string, unknown>;

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props<CheckboxModel, CheckboxValue>>(), {
	isError: false,
	errorMessage: '',
	required: false,
	disabled: false,
	checked: false,
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: CheckboxValue): void;
}>();

const { isFocused, isSelected, computedClass, handleInput } = useCheckbox(props, emit);
</script>

<template>
	<div class="field" :class="computedClass">
		<label class="field__control">
			<input
				v-bind="$attrs"
				:value="value"
				:checked="isSelected"
				type="checkbox"
				class="field__input"
				@input="handleInput"
				@focus="isFocused = true"
				@blur="isFocused = false"
			>
			<span class="field__checkbox" />
			<span class="field__label">
				<slot />
			</span>
		</label>

		<div v-if="isError" class="field__error">
			<template v-if="typeof errorMessage === 'object'">
				<div
					v-for="(message, index) in errorMessage"
					:key="index"
					class="mt-4"
				>
					{{ message }}
				</div>
			</template>
			<div v-else v-html="errorMessage" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.field {
	&--checked & {
		&__checkbox {
			border-color: $black;

			&::after {
				opacity: 1;
			}
		}
	}

	&--focused & {
		&__checkbox {
			border-color: $silver;
		}
	}

	&--error & {
		&__checkbox {
			border-color: $text-reddark;
		}
	}

	&--error#{&}--focused &,
	&--error#{&}--checked & {
		&__checkbox {
			border-color: $text-red;
		}
	}

	&--error#{&}--disabled & {
		&__checkbox {
			border-color: $text-reddark;
		}
	}

	&--required & {
		&__label {
			&::after {
				font-size: inherit;

				content: " *";

				color: $lust;
			}
		}
	}

	&--disabled & {
		&__control {
			cursor: default;
		}

		&__checkbox {
			border-color: $mine-shaft;
		}

		&__label {
			opacity: 0.6;
			color: $mine-shaft;
		}
	}

	&--disabled#{&}--checked & {
		&__checkbox {
			border-color: $mine-shaft;
		}

		&__label {
			opacity: 1;
		}
	}

	&__control {
		$indent: calc($checkbox-size + 10px);

		font-size: rem(16px);

		position: relative;

		display: inline-flex;

		padding-left: rem($indent);

		cursor: pointer;
	}

	&__input {
		@include visually-hidden;
	}

	&__checkbox {
		position: absolute;
		top: 0;
		left: 0;

		display: block;
		flex-shrink: 0;

		width: rem($checkbox-size);
		height: rem($checkbox-size);

		transition: border-color $transition-duration $transition-function;

		border: rem(1px) solid $black;

		&::after {
			$checkmark-size: map-get($input-check, 'size') - 2 * .25em;
			$checkmark-translate-y: math.div(map-get($input-check, 'size'), 2) - math.div($checkmark-size, 1.5);

			position: absolute;
			top: 0;
			left: 0;

			width: $checkmark-size;
			height: math.div($checkmark-size, 2);

			content: "";
			transform: translate(.1em, $checkmark-translate-y) rotate(-50deg);

			opacity: 0;
			border: rem(1px) solid $black;
			border-top-style: none;
			border-right-style: none;
		}
	}

	&__label {
		@include text-size($font-size-mini, $line-height-mini);

		color: $black;
	}

	&__error {
		@include text-size($font-size-mini, $line-height-mini);

		margin-top: rem($gap-small);

		color: $lust;
	}
}
</style>
